import * as React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Icon } from '@/components/Icon'
import { useUserStore } from '@/stores/userStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { WorkspaceType } from '@/api/types/workspace.types'

import { NavMain } from '@/components/Sidebar/NavMain'
import { NavUser } from '@/components/Sidebar/NavUser'
import { SpaceMain } from '@/components/Sidebar/SpaceMain'
import { HistoryMain } from '@/components/Sidebar/HistoryMain'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar'
import { Avatar, AvatarImage } from '@/components/ui/avatar'
import logo from '@/assets/images/sidebar/logo.png'
import { Button } from '@you/ui'

const BASE_MENU_ITEMS = [
  {
    title: '智能体中心',
    url: '/agents',
    icon: <Icon name="agent" />,
    isActive: true,
  },
  {
    title: '知识库',
    url: '/knowledge',
    icon: <Icon name="knowledge" />,
  },
  {
    title: '空间设置',
    url: '/workspace',
    icon: <Icon name="settings" />,
  },
  {
    title: '组织架构',
    url: '/organization',
    icon: <Icon name="tree" />,
  },
]

const DASHBOARD_ITEM = {
  title: '仪表盘',
  url: '/dashboard',
  icon: <Icon name="dashboard" />,
}

const HOME_AGENT_PATH = '/agents/chat/80'

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { open } = useSidebar()
  const location = useLocation()
  const isHome =
    location.pathname === '/' || location.pathname === HOME_AGENT_PATH
  const { currentUser } = useUserStore()
  const { currentWorkspace } = useWorkspaceStore()
  const navigate = useNavigate()

  const isOrgOwner =
    currentWorkspace?.type === WorkspaceType.Organization &&
    String(currentWorkspace.userId) === String(currentUser?.userId)

  const menuItems = isOrgOwner
    ? [DASHBOARD_ITEM, ...BASE_MENU_ITEMS]
    : BASE_MENU_ITEMS

  const handleNewChat = () => {
    navigate('/agents/chat/80')
  }

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className="flex justify-center items-center">
        {/* 头部logo部分 */}
        <Avatar className="h-[54px] w-[54px] rounded-full mt-5">
          <AvatarImage src={logo} alt={'Youknow'} />
        </Avatar>
        <p className="text-[20px] font-bold leading-10  transition-[margin,opacity] duration-200 ease-linear group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0">
          Youknow
        </p>
        <Button
          variant="outline"
          className="w-full h-12 rounded-[8px] bg-[#8752FF1A] text-[#3C19E9] text-lg px-1 overflow-hidden"
          onClick={handleNewChat}
        >
          <Icon name="chat" className="flex-none" />
          {open && <p className="ml-1">开启新对话</p>}
        </Button>
        <Link
          to="/"
          className={`flex items-center w-full rounded-sm ${
            isHome ? 'bg-[#8752FF1A] text-[#3D1E82]' : 'text-[#666666]'
          } h-12 px-2 mt-4`}
        >
          <Icon
            name="you"
            className={`${isHome ? 'text-[#8752FF]' : 'text-[#898DA4]'}`}
          />
          {open && <span className="ml-2 whitespace-nowrap">首页</span>}
        </Link>
      </SidebarHeader>
      {/* 中间菜单内容 */}
      <SidebarContent>
        <SidebarGroup className="border-t-2 border-b-2 border-[#8752FF1A]">
          <SpaceMain />
          <NavMain items={menuItems} ignoreActivePaths={[HOME_AGENT_PATH]} />
        </SidebarGroup>
        <HistoryMain />
      </SidebarContent>
      {/* 底部用户信息 */}
      <SidebarFooter>
        <NavUser
          user={{
            name: currentUser?.userName || '未知用户',
            email: currentUser?.phoneNo || '',
            avatar: currentUser?.avatar || '',
          }}
        />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
